import React from 'react';
import deepcopy from 'deepcopy';
import { connect } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import classnames from 'classnames';
import { GhostButton, RaisedButton } from '@hobs/components/Buttons';
import Tooltip from '@hobs/components/Tooltip';
import Status from '@hobs/components/Status';
import Indicator from '@hobs/components/Indicator';
import Icon from '@hobs/components/Icon';
import { H1 } from '@hobs/components/Headings';
import { ServiceRequestNavContext } from '@hobs/components/ServiceRequestNavContext';
import WithQuickSearch from '@hobs/hocs/WithQuickSearch';
import WithAnnexFilter from '@hobs/hocs/WithAnnexFilter';
import ObjectPath from 'object-path';
import Money from '@hobs/components/Money';
import Dropdown from '@hobs/components/Dropdown';
import InputLabel from '@hobs/components/InputLabel';
import { CellDate, CellDateTime } from '@hobs/components/Grid/CellDate';
import {
  PAGE_SIZES,
  USER_ROLES,
  DEFAULT_STATUS_FILTER,
  INDICATIONS,
  SERVICE_REQUEST_NAVIGATION_ORIGIN,
  DATE_FORMAT
} from '@hobs/utils/constants';
import {
  ExcelExport,
  ExcelExportColumn
} from '@progress/kendo-react-excel-export';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import WithRoles from '@hobs/hocs/WithRoles';
import WithVisibleColumns from '@hobs/hocs/WithVisibleColumns';
import PropTypes from 'prop-types';
import ShortId from 'shortid';
import Columns from '@hobs/components/Grid/Columns';
import {
  withDataStateLocalOverrides,
  withGridLocalOverrides,
  onUserResetSettings
} from '@hobs/utils/grid/overrides';
import { queryOData, queryXLSOData, resetOData } from '@hobs/actions/odata';
import { getAnnexes } from '@hobs/actions/annex';
import { getRequestedServicesGrid } from '@hobs/actions/gridSelections';
import Discussion from '@hobs/containers/Discussion';
import Summary from '@hobs/components/Summary';
import Toast from '@hobs/components/Toast';
import _ServiceRequests from '@hobs/containers/ServiceRequests';
import Quantity from '@hobs/components/Quantity';
import { expandDataItem } from '@hobs/utils/expandItem';
import { Icon as BpIcon } from '@blueprintjs/core';
import { formatLocal } from '@hobs/utils/formatWithTimeZone';
import ExerciseSelectionDialog from '@hobs/containers/ExerciseSelectionDialog/ExerciseSelectionDialog';
import { validateExerciseForSrCopy } from '@hobs/actions/serviceRequests';
import CopyServiceRequestsDialog from '@hobs/containers/CopyServiceRequestsDialog/CopyServiceRequestsDialog';
import Dialog from '@hobs/components/Dialog';
import Tabs from '@hobs/components/Tabs';
import BulkLocationChange from '@hobs/components/BulkLocationChange';
import { getSorSummary, getSorFinanceData } from '../../actions/sor';

const INDICATION_TRANSPORTATION_FIELD =
  'Indications.TransportationInformationMissing';

class RequestedServices extends _ServiceRequests {
  constructor(props) {
    super(props);
    const { t, hasUserPermission } = this.props;

    this.exportRef = React.createRef();
    this.grid = React.createRef();

    this.defaultGridColumns = {
      Indications: {
        title: t('Indications'),
        columnMenu: true,
        default: true,
        sortable: false,
        visible: true,
        minResizableWidth: '46px',
        headerCell: () => (
          <div>
            <span className="indicator-bubble"></span>
          </div>
        ),
        cell: ({ dataItem }) => {
          const indications = this.getIndications(dataItem.Indications);
          return (
            <td valign="middle">
              {indications.length > 0 && (
                <Indicator
                  message={indications.map(indication => (
                    <div key={ShortId.generate()}>
                      {INDICATIONS[indication]}
                    </div>
                  ))}
                  key={ShortId.generate()}
                />
              )}
            </td>
          );
        },
        customFilter: {
          data: [
            {
              name: t('All'),
              default: true,
              value: ''
            },
            {
              name: t('None'),
              default: false,
              value: false,
              field: INDICATION_TRANSPORTATION_FIELD
            },
            ...this.getIndicationColumnFilter()
          ]
        }
      },
      RequestNumber: {
        minResizableWidth: '70px',
        title: t('Req.'),
        columnMenu: true,
        width: '84px',
        cell: ({ dataItem }) => {
          const editURI = `/${dataItem.IsCustom ? 'custom-' : ''
            }service-request/${dataItem.ServiceRequestId}`;

          return (
            <td>
              {dataItem.RequestNumber &&
                !hasUserPermission(USER_ROLES.HOST_UNIT) && (
                  <ServiceRequestNavContext.Consumer>
                    {nav => (
                      <Link
                        to={editURI}
                        title={dataItem.RequestNumber}
                        onClick={() => {
                          nav.setPreviousGridPosition(
                            this.state.dataState,
                            window.pageYOffset,
                            this.state.selectedAnnex,
                            this.state.quickSearchValue,
                            SERVICE_REQUEST_NAVIGATION_ORIGIN.RequestedServices,
                            this.state.selectedUnit
                          );
                        }}
                      >
                        {dataItem.RequestNumber}
                      </Link>
                    )}
                  </ServiceRequestNavContext.Consumer>
                )}
              {dataItem.RequestNumber &&
                hasUserPermission(USER_ROLES.HOST_UNIT) && (
                  <>{dataItem.RequestNumber}</>
                )}

              {!dataItem.RequestNumber && <>-</>}
            </td>
          );
        },
        default: true,
        visible: true
      },
      HasAttachments: {
        minResizableWidth: '33px',
        title: t('Attachment'),
        cell: ({ dataItem }) => (
          <td colSpan="1">
            {dataItem.HasAttachments && (
              <Icon icon="attachment" className="service-requests-view--icon" />
            )}
          </td>
        ),
        headerCell: () => (
          <Icon icon="attachment" className="service-requests-view--icon" />
        ),
        default: false,
        visible: false,
        xls: false,
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            {
              name: t('Yes'),
              value: true
            },
            {
              name: t('No'),
              value: false
            }
          ]
        },
        columnMenu: true
      },
      AdditionalInfo: {
        title: t('Add. info.'),
        minResizableWidth: '45px',
        default: true,
        visible: true,
        xls: false,
        cell: ({ dataItem }) => (
          <td>
            {dataItem.Indications.IsEarlyConfirmationRequired && (
              <Tooltip
                tooltip={'Early confirmation required'}
                position="bottom"
              >
                <BpIcon icon="warning-sign" color="#b50026" />
              </Tooltip>
            )}
            {dataItem.AdditionalInfo && (
              <>
                {!hasUserPermission(USER_ROLES.READONLY) &&
                  !hasUserPermission(USER_ROLES.HOST_UNIT) ? (
                  <ServiceRequestNavContext.Consumer>
                    {nav => (
                      <Link
                        to={`/service-request/${dataItem.ServiceRequestId}/change-history`}
                        onClick={() => {
                          nav.setPreviousGridPosition(
                            this.state.dataState,
                            window.pageYOffset,
                            this.state.selectedAnnex,
                            this.state.quickSearchValue,
                            SERVICE_REQUEST_NAVIGATION_ORIGIN.RequestedServices,
                            this.state.selectedUnit
                          );
                          nav.setServiceRequestId(dataItem.ServiceRequestId);
                        }}
                      >
                        <Tooltip
                          tooltip={
                            dataItem.LatestTicketAction &&
                            `${dataItem.LatestTicketAction}${formatLocal(
                              dataItem.LatestTicketActionDate,
                              DATE_FORMAT
                            )})`
                          }
                          position="top"
                        >
                          <Icon
                            className={classnames(
                              'info-indication',
                              {
                                active: dataItem.AdditionalInfoActive
                              },
                              {
                                rejected: dataItem.AdditionalInfoRejected
                              }
                            )}
                            icon="InfoSmall"
                          />
                        </Tooltip>
                      </Link>
                    )}
                  </ServiceRequestNavContext.Consumer>
                ) : (
                  <Icon
                    className={classnames('info-indication', {
                      active: dataItem.AdditionalInfoActive
                    })}
                    icon="InfoSmall"
                  />
                )}
              </>
            )}
          </td>
        )
      },
      StatusId: {
        title: t('Status'),
        width: 103,
        columnMenu: true,
        cell: ({ dataItem }) => (
          <td>
            <Status
              name={dataItem.StatusGridName}
              className="service-requests-view--status"
              type={dataItem.StatusId}
            />
          </td>
        ),
        default: true,
        visible: true
      },
      Service: {
        title: t('Service'),
        columnMenu: true,
        default: true,
        visible: true
      },
      Description: {
        minResizableWidth: '80px',
        title: t('Description'),
        columnMenu: true,
        default: false,
        visible: false
      },
      Group: {
        title: t('Group'),
        cell: ({ dataItem }) => <td>{dataItem.Group}</td>,
        columnMenu: true,
        default: true,
        visible: true
      },
      AnnexId: {
        title: t('Annex'),
        cell: ({ dataItem }) => <td>{dataItem.Annex}</td>,
        columnMenu: true,
        default: false,
        visible: false,
        xlsField: 'Annex'
      },
      RequestedQuantity: {
        title: t('Qty'),
        columnMenu: true,
        default: true,
        visible: true,
        filter: 'numeric',
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Quantity amount={dataItem.RequestedQuantity} />
            </td>
          );
        }
      },
      IsCalculatedByDays: {
        title: t('Calculated by days'),
        default: true,
        visible: true,
        columnMenu: true,
        cell: ({ dataItem }) => {
          return <td>{dataItem.IsCalculatedByDays ? t('Yes') : t('No')}</td>;
        },
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            {
              name: t('Yes'),
              value: true
            },
            {
              name: t('No'),
              value: false
            }
          ]
        }
      },
      TotalQuantity: {
        title: t('Total q-ty'),
        filter: 'numeric',
        columnMenu: true,
        default: true,
        visible: true,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Quantity amount={dataItem.TotalQuantity} />
            </td>
          );
        }
      },
      DeliveryQuantity: {
        minResizableWidth: '100px',
        title: t('Delivery Qty'),
        filter: 'numeric',
        columnMenu: true,
        default: true,
        visible: true,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Quantity amount={dataItem.DeliveryQuantity} />
            </td>
          );
        }
      },
      IsDeliveryFinalized: {
        title: t('Delivery finalized'),
        default: false,
        visible: false,
        columnMenu: true,
        cell: ({ dataItem }) => {
          return <td>{dataItem.IsDeliveryFinalized ? t('Yes') : t('No')}</td>;
        },
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            {
              name: t('Yes'),
              value: true
            },
            {
              name: t('No'),
              value: false
            }
          ]
        }
      },
      UnitOfMeasureId: {
        title: t('UoM'),
        cell: ({ dataItem }) => <td>{dataItem.UnitOfMeasure}</td>,
        columnMenu: true,
        default: true,
        visible: true,
        xlsField: 'UnitOfMeasure'
      },
      UnallocatedQuantity: {
        minResizableWidth: '80px',
        title: t('Unall qty'),
        filter: 'numeric',
        columnMenu: true,
        default: false,
        visible: false,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Quantity amount={dataItem.UnallocatedQuantity} />
            </td>
          );
        }
      },
      Region: {
        title: t('Region'),
        columnMenu: true,
        default: false,
        visible: false
      },
      Location: {
        title: t('Location'),
        columnMenu: true,
        default: true,
        visible: true
      },
      LocationComment: {
        title: t('Location comment'),
        columnMenu: true,
        default: false,
        visible: false
      },
      LocationTo: {
        title: t('Delivery location'),
        columnMenu: true,
        default: true,
        visible: false
      },
      LocationCommentTo: {
        title: t('Delivery location comment'),
        columnMenu: true,
        default: false,
        visible: false
      },
      Site: {
        title: t('Site'),
        columnMenu: true,
        default: false,
        visible: false
      },
      Unit: {
        title: t('Unit'),
        default: true,
        columnMenu: true,
        visible: true
      },
      DateFrom: {
        title: t('From date'),
        columnMenu: true,
        minResizableWidth: '85px',
        filter: 'date',
        cell: CellDate,
        default: true,
        visible: true
      },
      DateTo: {
        title: t('To date'),
        columnMenu: true,
        minResizableWidth: '85px',
        filter: 'date',
        cell: CellDate,
        default: true,
        visible: true
      },
      Days: {
        minResizableWidth: '60px',
        title: t('Days'),
        filter: 'numeric',
        default: true,
        sortable: true,
        visible: true,
        columnMenu: true
      },
      UnitPrice: {
        title: t('Unit price'),
        columnMenu: true,
        filter: 'numeric',
        default: false,
        visible: false,
        xls: false,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Money amount={dataItem.UnitPrice} />
            </td>
          );
        }
      },
      EstimatedTotalCost: {
        filter: 'numeric',
        minResizableWidth: '100px',
        title: t('Est. cost'),
        default: true,
        sortable: false,
        visible: true,
        xls: false,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Money amount={dataItem.EstimatedTotalCostFrom} />
              {dataItem.EstimatedTotalCostFrom !==
                dataItem.EstimatedTotalCostTo ? (
                <>
                  {' '}
                  - <Money amount={dataItem.EstimatedTotalCostTo} />
                </>
              ) : (
                ''
              )}
            </td>
          );
        }
      },
      EstimatedGrossAmount: {
        minResizableWidth: '60px',
        title: t('Est. gross amount'),
        default: true,
        visible: true,
        sortable: false,
        xls: false,
        filter: 'numeric',
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Money amount={dataItem.EstimatedGrossAmountFrom} />
              {dataItem.EstimatedGrossAmountFrom !==
                dataItem.EstimatedGrossAmountTo ? (
                <>
                  {' '}
                  - <Money amount={dataItem.EstimatedGrossAmountTo} />
                </>
              ) : (
                ''
              )}
            </td>
          );
        }
      },
      ActualCost: {
        minResizableWidth: '60px',
        title: t('Actual cost'),
        columnMenu: true,
        default: true,
        visible: true,
        filter: 'numeric',
        xls: false,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Money amount={dataItem.ActualCost} />
            </td>
          );
        }
      },
      ActualGrossAmount: {
        minResizableWidth: '60px',
        title: t('Act. gross amount'),
        columnMenu: true,
        default: true,
        visible: true,
        filter: 'numeric',
        xls: false,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Money amount={dataItem.ActualGrossAmount} />
            </td>
          );
        }
      },
      IsPaid: {
        title: t('Is Paid'),
        default: false,
        visible: false,
        columnMenu: true,
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            {
              name: t('Yes'),
              value: true
            },
            {
              name: t('No'),
              value: false
            }
          ]
        },
        cell: ({ dataItem }) => {
          return <td>{dataItem.IsPaid ? t('Yes') : t('No')}</td>;
        }
      },
      IsSharedCost: {
        title: t('Is Shared Cost'),
        default: false,
        columnMenu: true,
        visible: false,
        cell: ({ dataItem }) => {
          return <td>{dataItem.IsSharedCost ? t('Yes') : t('No')}</td>;
        },
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            {
              name: t('Yes'),
              value: true
            },
            {
              name: t('No'),
              value: false
            }
          ]
        }
      },
      InvoiceNumber: {
        title: t('Invoice ref.'),
        columnMenu: true,
        default: false,
        visible: false
      },
      ActivityName: {
        title: t('Activity'),
        columnMenu: true,
        default: false,
        visible: false
      },
      IsEarlyConfirmationRequired: {
        title: t('Is Early Confirmation Required'),
        default: false,
        visible: false,
        columnMenu: true,
        cell: ({ dataItem }) => {
          return (
            <td>
              {dataItem.Indications &&
                dataItem.Indications.IsEarlyConfirmationRequired
                ? t('Yes')
                : t('No')}
            </td>
          );
        },
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            {
              name: t('Yes'),
              value: true
            },
            {
              name: t('No'),
              value: false
            }
          ]
        }
      }
    };

    if (
      !hasUserPermission(USER_ROLES.READONLY) &&
      !hasUserPermission(USER_ROLES.HOST_UNIT)
    ) {
      Object.assign(this.defaultGridColumns, {
        DiscussionStatus: {
          title: t('Disc.'),
          default: true,
          columnMenu: true,
          visible: true,
          xls: false,
          cell: ({ dataItem }) => {
            return (
              <td>
                <Link to={this.getDiscussionPath(dataItem.ServiceRequestId)}>
                  <Discussion
                    serviceRequestId={dataItem.ServiceRequestId}
                    status={dataItem.DiscussionStatus}
                    iconOnly={true}
                    initials={t('HN')}
                    onDiscussionClose={this.queryOData}
                    isSeen={dataItem.IsLastDiscussionSeen}
                  />
                </Link>
              </td>
            );
          },
          customFilter: {
            data: [
              {
                name: t('Select all'),
                default: true,
                value: ''
              },
              {
                name: t('Unread messages'),
                value: 1
              },
              {
                name: t('Read messages'),
                value: 2
              },
              {
                name: t('No messages'),
                value: 3
              }
            ]
          }
        }
      });
    }

    Object.assign(this.defaultGridColumns, {
      CreationDate: {
        title: t('Creation date'),
        columnMenu: true,
        default: false,
        visible: false,
        filter: 'date',
        cell: CellDateTime
      },
      LastChangeDate: {
        title: t('Last change'),
        columnMenu: true,
        default: false,
        visible: false,
        filter: 'date',
        cell: CellDateTime
      },
      DeliveryComment: {
        title: t('Delivered comment'),
        columnMenu: true,
        default: false,
        visible: false
      },
      Comment: {
        title: t('Comment'),
        columnMenu: true,
        default: false,
        visible: false
      },
      ResponseComment: {
        title: t('Response comment'),
        columnMenu: true,
        default: false,
        visible: false
      }
    });

    this.defaultDataState = {
      take: 20,
      skip: 0,
      filter: {
        logic: 'and',
        filters: [DEFAULT_STATUS_FILTER]
      },
      sort: [{ field: 'RequestNumber', dir: 'desc' }]
    };

    this.state = {
      filters: {},
      selectedUnit: '',
      selectedAnnex: null,
      dataState: withDataStateLocalOverrides(
        deepcopy(this.defaultDataState),
        this.LSdataStateName
      ),
      showConfirmDialog: false,
      gridKey: ShortId.generate(),
      columns: withGridLocalOverrides(
        deepcopy(this.defaultGridColumns),
        this.LSGridPropsName
      ),
      isConfirmIsorDisabled: false,
      isCreateSrDisabled: false,
      selectedRows: {},
      isAllSelected: false,
      IsExerciseSelectionDialogOpen: false,
      IsServiceRequestCopyDialogOpen: false,
      selectedExerciseToCopy: {},
      isBulkAssignOpen: false,
    };
  }

  LSdataStateName = `ServiceRequestsSN${this.props.user.CurrentExerciseId}.dataState`;
  LSGridPropsName = `ServiceRequestsSN${this.props.user.CurrentExerciseId}.grid.props`;

  getServiceRequestsUrl() {
    return 'ServiceRequestQuery';
  }

  async componentDidMount() {
    await this.queryOData();
    await this.props.getAnnexes();
    await this.props.getRequestedServicesGrid();
    this.applyCustomFilters();

    await this.props.getSorSummary();
    await this.props.getSorFinanceData();
    await this.props.withVisibleColumns.setColumns(this.state.columns);
    this.props.withVisibleColumns.onUpdate(() => {
      this.setState({
        gridKey: ShortId.generate()
      });
    });

    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleScroll);
  }

  componentWillUnmount() {
    this.props.resetOData();
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleScroll);
  }

  onUserSaveSettings = () => {
    localStorage.setItem(
      this.LSdataStateName,
      JSON.stringify(this.state.dataState)
    );
    localStorage.setItem(
      this.LSGridPropsName,
      JSON.stringify({
        columns: this.grid.current.columns
      })
    );
  };

  onUserResetSettings = onUserResetSettings(
    this.LSdataStateName,
    this.LSGridPropsName,
    this.NotificationsQueryFilterName,
    () => {
      this.props.withVisibleColumns.setColumns(
        Object.assign(
          {},
          this.getColumnsWithCustomFilter(this.defaultGridColumns)
        )
      );

      this.setState(
        {
          gridKey: ShortId.generate(),
          columns: this.getColumnsWithCustomFilter(this.defaultGridColumns),
          dataState: this.defaultDataState
        },
        () => {
          this.queryOData();
        }
      );
    }
  );

  canCreateServiceRequest = () => {
    const { sor } = this.props;
    const { isCreateSrDisabled } = this.state;
    return (
      (!!ObjectPath.get(sor, 'sorSummary.canCreateServiceRequest') ||
        !!ObjectPath.get(sor, 'sorSummary.canCreateAdditionalServiceRequest') ||
        !!ObjectPath.get(sor, 'sorSummary.isExerciseIsFrozen')) &&
      !isCreateSrDisabled
    );
  };

  getSummaryInfo() {
    const { selectedAnnex } = this.state;
    const { currentExercise, t } = this.props;

    const phone = ObjectPath.get(selectedAnnex, 'responsiblePerson.phone')
      ? this.getFieldValue(selectedAnnex.responsiblePerson.phone)
      : this.getFieldValue(
        ObjectPath.get(
          currentExercise,
          'hostInformation.responsiblePersonPhone'
        )
      );
    const name = ObjectPath.get(selectedAnnex, 'responsiblePerson.name')
      ? this.getFieldValue(selectedAnnex.responsiblePerson.name)
      : this.getFieldValue(
        ObjectPath.get(
          currentExercise,
          'hostInformation.responsiblePersonName'
        ),
        t('Responsible person')
      );

    const email = ObjectPath.get(selectedAnnex, 'responsiblePerson.email')
      ? this.getFieldValue(selectedAnnex.responsiblePerson.email)
      : this.getFieldValue(
        ObjectPath.get(
          currentExercise,
          'hostInformation.responsiblePersonEmail'
        )
      );

    const organization = ObjectPath.get(
      currentExercise,
      'hostInformation.responsiblePersonOrganization'
    )
      ? ObjectPath.get(
        currentExercise,
        'hostInformation.responsiblePersonOrganization'
      ) + t(' responsible')
      : this.getFieldValue(
        ObjectPath.get(
          currentExercise,
          'hostInformation.responsiblePersonOrganization'
        ),
        t('Responsible person')
      );

    return {
      responsiblePerson: {
        phone,
        name,
        email
      },
      requestedQuantity: this.props.oData.total,
      annexName:
        selectedAnnex && selectedAnnex.name ? selectedAnnex.name : null,
      organization,
      sendingNationName: ObjectPath.get(this.props.sor, 'sorSummary.partyName')
    };
  }

  canManageApprovals() {
    return !!ObjectPath.get(this.props.sor, 'sorSummary.canManageApprovals');
  }

  getDiscussionPath(srId) {
    const { match } = this.props;
    return `${match.url}/${srId}/discussion`;
  }

  render() {
    const {
      t,
      withVisibleColumns: { getVisibleColumnCount, openDialog, getGridColumns },
      hasUserPermission,
      sor,
      isLoading,
      currentExercise
    } = this.props;
    const { selectedUnit } = this.state;

    const selectedServiceRequests = this.getSelectedServiceRequestIds();

    return (
      <section>
        {this.scrollWindow()}
        {this.clearSavedOffset()}
        <ServiceRequestNavContext.Consumer>
          {nav => !isLoading && this.navigateToSavedLocation(nav)}
        </ServiceRequestNavContext.Consumer>
        <ServiceRequestNavContext.Consumer>
          {nav => this.setSavedDataState(nav)}
        </ServiceRequestNavContext.Consumer>
        <ServiceRequestNavContext.Consumer>
          {nav =>
            nav.serviceRequestNavigation !==
            SERVICE_REQUEST_NAVIGATION_ORIGIN.RequestedServices &&
            nav.setServiceRequestNavigation(
              SERVICE_REQUEST_NAVIGATION_ORIGIN.RequestedServices
            )
          }
        </ServiceRequestNavContext.Consumer>
        <H1>{t('Requested services')}</H1>
        <div className="service-requests-view">
          <Summary info={this.getSummaryInfo()}>
            {!hasUserPermission(USER_ROLES.READONLY) &&
              !hasUserPermission(USER_ROLES.HOST_UNIT) &&
              this.canManageApprovals() && (
                <GhostButton to={'/sor-requisites?tab=approvals'}>
                  {t('SOR approvals')}
                </GhostButton>
              )}
          </Summary>
          <div className="service-requests-view--heading">
            <div className="service-requests-view--heading-buttons">
              {!hasUserPermission(USER_ROLES.READONLY) &&
                !hasUserPermission(USER_ROLES.HOST_UNIT) && (
                  <RaisedButton
                    disabled={!this.canCreateServiceRequest()}
                    to="/service-request"
                    type="button"
                    key={ShortId.generate()}
                    iconLeft={<Icon icon="Plus" />}
                    onClick={event => {
                      if (
                        ObjectPath.get(sor, 'sorSummary.isExerciseIsFrozen')
                      ) {
                        event.preventDefault();
                        Toast.warning(
                          t(
                            'New services cannot be added, because FSOR is currently frozen'
                          )
                        );
                      }
                    }}
                  >
                    {ObjectPath.get(
                      sor,
                      'sorSummary.canCreateAdditionalServiceRequest'
                    )
                      ? t('Add additional request')
                      : t('Add service request')}
                  </RaisedButton>
                )}
              <GhostButton
                iconLeft={<Icon icon="plus" />}
                disabled={
                  (!Object.values(this.state.selectedRows).includes(true) &&
                    !this.state.isAllSelected) ||
                  (!hasUserPermission(USER_ROLES.ADMIN) &&
                    !hasUserPermission(USER_ROLES.SystemAdmin))
                }
                onClick={this.openExerciseSelectionDialog}
              >
                {t('Copy to another exercise')}
              </GhostButton>
            </div>

            <div className="service-requests-view--heading-actions">
              <div className="service-requests-view--heading-filters">
                <InputLabel>{t('Show:')}</InputLabel>
                <ServiceRequestNavContext.Consumer>
                  {nav => (
                    <Dropdown
                      wrapperClass="service-requests-view--heading-filters-dropdown"
                      large
                      value={selectedUnit}
                      onChange={event => {
                        nav.clearSelectedUnit();
                        this.handleUnitFilterChange(event.target.value);
                      }}
                    >
                      {this.getUnitOptions(
                        ObjectPath.get(this.props.sor, 'sorSummary.units')
                      )}
                    </Dropdown>
                  )}
                </ServiceRequestNavContext.Consumer>
                <div>
                  {this.props.withQuickSearchForm({
                    onSearch: this.onQuickSearch,
                    noMargin: true,
                    preFilledQuickSearch: this.state.preFilledQuickSearch
                  })}
                </div>
              </div>
              {(hasUserPermission(USER_ROLES.ADMIN) || hasUserPermission(USER_ROLES.USER)) && (
                <RaisedButton
                  disabled={this.state.showAllRequests}
                  type="button"
                  onClick={() => {
                    if (
                      !Object.values(this.state.selectedRows).includes(true) ||
                      this.state.isAllSelected
                    ) {
                      Toast.warning(t("Please select specific service requests first"))
                      return;
                    }

                    this.setState({ isBulkAssignOpen: true })
                  }}
                >
                  {t('Bulk Action')}
                </RaisedButton>
              )}
              <Dialog
                title={t('Bulk Action')}
                isOpen={this.state.isBulkAssignOpen}
                onClose={() => this.setState({ isBulkAssignOpen: false })}
              >
                <Tabs className="user-tabs">
                  <Tabs.Tab
                    component={
                      <BulkLocationChange
                        getSelectedServiceRequestIds={this.state.isAllSelected ? null : this.getSelectedServiceRequestIds}
                        oData={this.props.oData}
                        onSuccess={() => {
                          this.queryOData();
                          this.setState({ isBulkAssignOpen: false })
                        }}
                      />
                    }
                  >
                    {t('Location')}
                  </Tabs.Tab>
                </Tabs>
              </Dialog>
              <GhostButton
                onClick={() => {
                  openDialog();
                }}
                type="button"
              >
                {t('Visible columns')} ({getVisibleColumnCount()})
              </GhostButton>

              <GhostButton
                disabled={this.getDisplayData().length === 0}
                iconLeft={<Icon icon="download" />}
                onClick={this.exportServiceRequests}
              >
                {t('XLSX')}
              </GhostButton>
            </div>
          </div>

          <div>
            {this.props.withAnnexFilterForm((id, annex) => {
              const { selectedAnnex, preselectedAnnex } = this.state;

              // Additional condition to allow keep page then we are returning
              // from annex after it's preview.
              const shouldSetSkip =
                preselectedAnnex &&
                selectedAnnex &&
                selectedAnnex.annexId === preselectedAnnex.annexId;
              const additionalDataStateOverride = {};

              if (shouldSetSkip) {
                additionalDataStateOverride.skip = 0;
              }
              // End of condition;

              this.setState(
                {
                  selectedAnnex: annex == selectedAnnex ? null : annex,
                  preselectedAnnex: null,
                  dataState: {
                    ...this.state.dataState,
                    ...additionalDataStateOverride
                  }
                },
                this.queryOData
              );
            }, this.state.preselectedAnnex)}
          </div>

          {Object.keys(getGridColumns).length > 0 && (
            <>
              <ExcelExport
                ref={this.exportRef}
                data={this.state.XLSoData}
                fileName={this.getExportFileName()}
              >
                {Object.keys(getGridColumns).reduce((results, key) => {
                  const column = getGridColumns[key];
                  if (
                    (typeof column.xls === 'undefined' || column.xls) &&
                    column.visible
                  ) {
                    if (column.filter === 'date') {
                      results.push(
                        <ExcelExportColumn
                          key={ShortId.generate()}
                          width={125}
                          field={column.xlsField || key}
                          title={column.title}
                        />
                      );
                    } else {
                      results.push(
                        <ExcelExportColumn
                          key={ShortId.generate()}
                          field={column.xlsField || key}
                          title={column.title}
                        />
                      );
                    }
                  }
                  return results;
                }, [])}
                {getGridColumns.ActualCost.visible && (
                  <ExcelExportColumn
                    key={ShortId.generate()}
                    field="ActualCost"
                    title={t('Actual cost')}
                    footer={() => {
                      return (
                        t('Sum: ') +
                        this.getTotalEstimatedTotalCost().FooterTotalActualCost
                      );
                    }}
                  />
                )}
                <ExcelExportColumn
                  key={ShortId.generate()}
                  field="EstimatedTotalCostFrom"
                  title={t('Est. cost from')}
                  footer={() => {
                    return (
                      t('Sum: ') +
                      this.getTotalEstimatedTotalCost()
                        .FooterTotalEstimatedCostFrom
                    );
                  }}
                />
                <ExcelExportColumn
                  key={ShortId.generate()}
                  field="EstimatedTotalCostTo"
                  title={t('Est. cost to')}
                  footer={() => {
                    return (
                      t('Sum: ') +
                      this.getTotalEstimatedTotalCost()
                        .FooterTotalEstimatedCostTo
                    );
                  }}
                />
                <ExcelExportColumn
                  key={ShortId.generate()}
                  field="EstimatedGrossAmountFrom"
                  title={t('Est. gross amount from')}
                  footer={() => {
                    return (
                      t('Sum: ') +
                      this.getTotalEstimatedTotalCost()
                        .FooterTotalEstimatedGrossAmountFrom
                    );
                  }}
                />
                <ExcelExportColumn
                  key={ShortId.generate()}
                  field="EstimatedGrossAmountTo"
                  title={t('Est. gross amount to')}
                  footer={() => {
                    return (
                      t('Sum: ') +
                      this.getTotalEstimatedTotalCost()
                        .FooterTotalEstimatedGrossAmountTo
                    );
                  }}
                />
              </ExcelExport>
              <ServiceRequestNavContext.Consumer>
                {nav => (
                  <Grid
                    key={this.state.gridKey}
                    ref={this.grid}
                    resizable={true}
                    sortable={true}
                    selectable={true}
                    selectedField={
                      this.state.showAllRequests ? null : 'selectedField'
                    }
                    onHeaderSelectionChange={() =>
                      this.handleSelectAll(this.props.oData.data)
                    }
                    onSelectionChange={({ dataItem }) => {
                      this.handleSelect(dataItem);
                    }}
                    reorderable={true}
                    filter={DEFAULT_STATUS_FILTER}
                    pageable={{ pageSizes: PAGE_SIZES }}
                    {...this.state.dataState}
                    total={this.props.oData.total}
                    data={this.getDisplayData()}
                    onDataStateChange={this.dataStateChange}
                    rowRender={(row, props) => {
                      if (props.dataItem.footer) {
                        return (
                          <tr className="k-master-row">
                            <td colSpan={getVisibleColumnCount() + 1}>
                              <div className="service-requests-view--footer">
                                <div className="service-requests-view--footer--title">
                                  {t('Sum')}
                                  {` [${currentExercise.currency.name}]`}:
                                </div>
                                <div className="service-requests-view--footer--items">
                                  <div className="service-requests-view--footer--item">
                                    {getGridColumns.ActualCost.visible && (
                                      <Tooltip
                                        tooltip={t('Actual cost')}
                                        position="top"
                                      >
                                        <Money
                                          amount={
                                            props.dataItem.FooterTotalActualCost
                                          }
                                        />
                                      </Tooltip>
                                    )}
                                  </div>
                                  <div className="service-requests-view--footer--item">
                                    <Tooltip
                                      tooltip={t('Est. cost')}
                                      position="top"
                                    >
                                      <>
                                        <Money
                                          amount={
                                            props.dataItem
                                              .FooterTotalEstimatedCostFrom
                                          }
                                        />

                                        {props.dataItem
                                          .FooterTotalEstimatedCostFrom !==
                                          props.dataItem
                                            .FooterTotalEstimatedCostTo && (
                                            <>
                                              {' - '}
                                              <Money
                                                amount={
                                                  props.dataItem
                                                    .FooterTotalEstimatedCostTo
                                                }
                                              />
                                            </>
                                          )}
                                      </>
                                    </Tooltip>
                                  </div>
                                  <div className="service-requests-view--footer--item">
                                    <Tooltip
                                      tooltip={t('Est. gross amount')}
                                      position="top"
                                    >
                                      <>
                                        <Money
                                          amount={
                                            props.dataItem
                                              .FooterTotalEstimatedGrossAmountFrom
                                          }
                                        />

                                        {props.dataItem
                                          .FooterTotalEstimatedGrossAmountFrom !==
                                          props.dataItem
                                            .FooterTotalEstimatedGrossAmountTo && (
                                            <>
                                              {' - '}
                                              <Money
                                                amount={
                                                  props.dataItem
                                                    .FooterTotalEstimatedGrossAmountTo
                                                }
                                              />
                                            </>
                                          )}
                                      </>
                                    </Tooltip>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      }

                      return {
                        ...row,
                        props: {
                          ...row.props,
                          className: classnames(row.props.className, {
                            'service-requests-view--disabled':
                              props.dataItem.StatusId === 2,
                            'service-requests-view--last-visited':
                              props.dataItem.ServiceRequestId ===
                              nav.serviceRequestId,
                            'service-requests-view--early-confirmation-required':
                              props.dataItem.Indications
                                .IsEarlyConfirmationRequired
                          })
                        }
                      };
                    }}
                  >
                    <GridColumn
                      field="selectedField"
                      headerCell={this.state.showAllRequests ? 'empty' : null}
                      headerSelectionValue={
                        this.state.isAllSelected &&
                        selectedServiceRequests.length === 0
                      }
                      width="30px"
                    />
                    {Columns({
                      columns: getGridColumns,
                      onSave: this.onUserSaveSettings,
                      onReset: this.onUserResetSettings,
                      dataState: this.state.dataState
                    })}
                  </Grid>
                )}
              </ServiceRequestNavContext.Consumer>
            </>
          )}
        </div>
        <ExerciseSelectionDialog
          isOpen={this.state.IsExerciseSelectionDialogOpen}
          onClose={() =>
            this.setState({ IsExerciseSelectionDialogOpen: false })
          }
          onSubmit={async exercise => {
            await this.props
              .validateExerciseForSrCopy(exercise.exerciseId)
              .then(response => {
                if (response && response.success) {
                  this.setState({
                    selectedExerciseToCopy: exercise,
                    IsServiceRequestCopyDialogOpen: true
                  });
                } else if (response && response.payload) {
                  Toast.warning(response.payload.message);
                } else {
                  Toast.error('Error validating exercise');
                }
              });
          }}
          exercises={this.props.partyExercises}
        />
        <CopyServiceRequestsDialog
          isOpen={this.state.IsServiceRequestCopyDialogOpen}
          onClose={() =>
            this.setState({ IsServiceRequestCopyDialogOpen: false })
          }
          exercise={this.state.selectedExerciseToCopy}
          serviceRequestIds={this.getSelectedServiceRequestIds(
            this.props.oData,
            this.state.isAllSelected
          )}
        />
      </section>
    );
  }
}

RequestedServices.propTypes = {
  isLoading: PropTypes.bool,
  queryOData: PropTypes.func,
  queryXLSOData: PropTypes.func,
  resetOData: PropTypes.func,
  withQuickSearchForm: PropTypes.func,
  withAnnexFilterForm: PropTypes.func,
  getAnnexes: PropTypes.func,
  oData: PropTypes.object,
  withAnnexFilterSelected: PropTypes.string,
  withAnnexFilterOdata: PropTypes.func,
  withQuickSearchFilter: PropTypes.object,
  withVisibleColumns: PropTypes.object,
  serviceRequests: PropTypes.object,
  sor: PropTypes.object,
  t: PropTypes.func,
  match: PropTypes.object,
  location: PropTypes.object,
  currentExercise: PropTypes.object,
  gridSelections: PropTypes.object,
  hasUserPermission: PropTypes.func,
  getRequestedServicesGrid: PropTypes.func,
  getSorSummary: PropTypes.func,
  getSorFinanceData: PropTypes.func,
};

const mapStateToProps = state => ({
  currentExercise: state.exercise.current,
  oData: state.odata,
  sor: state.sor,
  gridSelections: state.gridSelections.requestedServicesGrid,
  partyExercises: state.exercises.list,
});

const mapDispatchToProps = {
  queryOData,
  queryXLSOData,
  resetOData,
  getAnnexes,
  getSorFinanceData,
  getRequestedServicesGrid,
  getSorSummary,
  validateExerciseForSrCopy,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  withTranslation()(
    withRouter(
      WithQuickSearch(
        WithAnnexFilter(WithVisibleColumns(WithRoles(RequestedServices)))
      )
    )
  )
);
